import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DateTime } from "luxon";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DateRangeType } from "react-tailwindcss-datepicker";

import { CheckIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper, RowModel } from "@tanstack/react-table";

import useBaskets from "../../data/useBaskets";
import useCategoriesPartial from "../../data/useCategoriesPartial";
import useFlowTypeAccounts from "../../data/useFlowTypeAccounts";
import usePartDetails from "../../data/usePartDetails";
import usePartMutations from "../../data/usePartMutations";
import useWarehouseLocations from "../../data/useWarehouseLocations";
import useWorkOrders from "../../data/useWorkOrders";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import { useLabelGenerator } from "../../hooks/useLabelGenerator";
import Modal from "../../layouts/Modal";
import { FormatType, ListingStatusEnum } from "../../models/EbayOffer";
import { IWarehouseLocation } from "../../models/Location";
import { PageMode } from "../../models/PagedTable";
import { IDeletePartResponse, IPart, IPartCategory, PartStatus } from "../../models/Part";
import { WorkOrderStatus } from "../../models/WorkOrder";
import classNames from "../../utils/classNames";
import openInNewTab from "../../utils/openInNewTab";
import PriceFormat from "../../utils/priceFormat";
import { currencySymbol } from "../../utils/currency";
import { MAX_PART_PRICE } from "../../utils/priceLimits";
import toIsoDate from "../../utils/toIsoDate";
import ContactFromId from "../contacts/ContactFromId";
import useNotification from "../notifications/useNotifications";
import EditListingImages from "../ebayListings/EditListingImages";
import UnlistedOfferPrice from "../ebayListings/unlistedTable/UnlistedOfferPrice";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import DateRangeFilter from "../shared/DateRangeFilter";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { SearchPaletteItem } from "../shared/Inputs/SearchPaletteTypes";
import SelectInput from "../shared/Inputs/SelectInput";
import TriToggleInput, { type TriState } from "../shared/Inputs/TriToggleInput";
import LoadingWheel from "../shared/LoadingWheel";
import MarketplacePills from "../shared/MarketplacePills";
import PartGradeBadge from "../shared/PartGradeBadge";
import PartNotesIndicator from "../shared/PartNotesIndicator";
import DamageNoteIndicator from "../shared/DamageNoteIndicator";
import TableCheckboxCol from "../shared/Tables/IndeterminateCheckbox";
import TableFilterPanel from "../shared/Tables/TableFilterPanel";
import VehiclePagedTable from "../shared/Tables/VehiclePagedTable";
import WarehouseLocationFromId from "../warehouseLocations/WarehouseLocationFromId";
import WarehouseLocationSearch from "../warehouseLocations/WarehouseLocationSearch";
import BulkAddToWorkOrderModal from "../workOrders/BulkAddToWorkOrderModal";
import DonorVehicleCell, { type VehicleSearchHighlights } from "./DonorVehicleCell";
import PartImage from "./PartImage";
import SendToQcModal from "./SendToQcModal";
import StatusIcons from "./StatusIcons";
import ChangeCategoryModal from "./stockFunctions/ChangeCatgeoryModal";
import ChangeWarehouseLocationModal from "./stockFunctions/ChangeWarehouseLocationModal";
import StockMpnCell from "./StockMpnCell";
import VehiclePartSearchFilters, { buildVehiclePartSearchBody, defaultVehicleDetailFilterEnabled, IVehiclePartSearchFields, VehicleDetailFilterKey } from "./VehiclePartSearchFilters";
import printHtml from "../../utils/printHtml";

const colHelper = createColumnHelper<IPart>();

type LocationSearchMode = "equals" | "beginsWith";

interface IFormFields extends IVehiclePartSearchFields {
  make: string;
  model: string;
  trim: string;
  year: string;
  bodyStyle: string;
  numberOfDoors: string;
  fuelType: string;
  transmissionType: string;
  engineSize: string;
  engineCode: string;
  yearFrom: string;
  yearTo: string;
  partNumber: string;
  partCategory: IPartCategory[];
  vrm: string;
  tagNumber: string;
  smartSearch: string;
  dvlaYearOfManufacture: string;
  visibilityDate: string;
  terminateDate: string;
  listingStatus: ListingStatusEnum;
  locationSearchMode: LocationSearchMode;
  warehouseLocation: IWarehouseLocation | undefined;
  warehouseLocationId: string | undefined;
  warehouseLocationBeginsWith: string;
  dateFieldType: string | undefined;
  hasWorkOrder: TriState;
  hasImages: TriState;
  hasMPN: TriState;
  hasLocation: TriState;
  partStatus: number;
  accountId?: string;
  workOrderId?: string;
}

const defaultFormValues: IFormFields = {
  make: "",
  model: "",
  trim: "",
  year: "",
  bodyStyle: "",
  numberOfDoors: "",
  fuelType: "",
  transmissionType: "",
  engineSize: "",
  engineCode: "",
  yearFrom: "",
  yearTo: "",
  partNumber: "",
  partCategory: [],
  vrm: "",
  smartSearch: "",
  dvlaYearOfManufacture: "",
  visibilityDate: "",
  terminateDate: "",
  tagNumber: "",
  listingStatus: ListingStatusEnum["All"],
  locationSearchMode: "equals",
  warehouseLocation: undefined,
  warehouseLocationId: undefined,
  warehouseLocationBeginsWith: "",
  dateFieldType: "Created",
  hasWorkOrder: "any",
  hasImages: "any",
  hasMPN: "any",
  hasLocation: "any",
  partStatus: -1,
  accountId: undefined,
  workOrderId: "",
};

interface IStockTableProps {
  showActions?: boolean;
  showStatus?: boolean;
  tableKey?: string;
  tableUrl?: string;
  pageMode?: PageMode;
  externalFilters?: Record<string, any>;
  filterBanner?: ReactNode;
  editableMpn?: boolean;
  showQcReason?: boolean;
  defaultPartStatus?: number;
  /** When given (the till's full search), each row gets an add-to-basket button in its own column. */
  onAddToBasket?: (part: IPart) => void;
  /** Lower-cased part ids already in the basket, so the button shows "In basket" instead. */
  inBasketIds?: Set<string>;
}

interface IStockSavedDefaultFilters {
  listingStatus: ListingStatusEnum;
  partStatus: number;
  hasWorkOrder: TriState;
  hasImages: TriState;
  hasMPN: TriState;
  hasLocation: TriState;
  locationSearchMode: LocationSearchMode;
  warehouseLocation: IWarehouseLocation | undefined;
  warehouseLocationBeginsWith: string;
  startDate: string | null;
  endDate: string | null;
  dateFieldType: string;
  accountId?: string;
  workOrderId?: string;
}

function getDefaultPartStatusBody(defaultPartStatus: number): Record<string, number> | Record<string, never> {
  return typeof defaultPartStatus === "number" && defaultPartStatus >= 0
    ? { partStatus: defaultPartStatus }
    : {};
}

function buildTriStateFlags(state: {
  hasWorkOrder: TriState | undefined;
  hasImages: TriState | undefined;
  hasMPN: TriState | undefined;
  hasLocation: TriState | undefined;
}): Record<string, true | undefined> {
  const map = (value: TriState | undefined, withKey: string, withoutKey: string): Record<string, true> => {
    if (value === "with") return { [withKey]: true };
    if (value === "without") return { [withoutKey]: true };
    return {};
  };

  return {
    ...map(state.hasWorkOrder, "partsWithWorkOrder", "partsWithoutWorkOrder"),
    ...map(state.hasImages, "partsWithImages", "partsWithoutImages"),
    ...map(state.hasMPN, "partsWithMPN", "partsWithoutMPN"),
    ...map(state.hasLocation, "partsWithLocation", "partsWithoutLocations"),
  };
}

function FilterSegmented<T extends string | number>({
  label,
  value,
  onChange,
  segments,
  disabled = false,
}: {
  label: string;
  value: T | undefined;
  onChange: (next: T) => void;
  segments: { value: T; label: string }[];
  disabled?: boolean;
}) {
  const { t } = useTranslation("stock");

  return (
    <div className="flex flex-col">
      <span className="text-sm font-medium text-black">{label}</span>
      <div
        role="group"
        aria-label={label}
        className={classNames("mt-1 inline-flex rounded-md border border-gray-300 bg-white p-0.5", disabled ? "opacity-50" : "")}
      >
        {segments.map((seg) => {
          const active = value === seg.value;
          return (
            <button
              key={String(seg.value)}
              type="button"
              disabled={disabled}
              onClick={() => onChange(seg.value)}
              className={classNames(
                "rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors whitespace-nowrap",
                active ? "bg-primary-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-100",
                disabled ? "cursor-not-allowed" : "cursor-pointer"
              )}
              aria-pressed={active}
            >
              {t(seg.label)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CopyText({ text, className = "", title }: { text: string | number | null | undefined; className?: string; title?: string }) {
  const { t } = useTranslation("stock");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1200);
    return () => clearTimeout(timer);
  }, [copied]);

  if (text === null || text === undefined || text === "") return null;
  const value = String(text);

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(value).then(() => setCopied(true));
      }}
      title={title ?? (copied ? t("Copied!") : t("Click to copy"))}
      className={classNames(
        "inline-flex items-center gap-1 rounded px-1 -mx-1 text-left transition-all duration-150 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-400",
        copied ? "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200" : "hover:bg-slate-100 hover:text-primary-600",
        className
      )}
    >
      {copied ? (
        <>
          <CheckIcon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
          <span className="font-medium">{t("Copied!")}</span>
        </>
      ) : (
        <span className="truncate">{value}</span>
      )}
    </button>
  );
}

function StockImagesExpandedRow({ part }: { part: IPart }) {
  const { t } = useTranslation("stock");
  const { partDetails, isLoading, isError } = usePartDetails(part.id, true);
  const fullPart = partDetails || part;

  if (isLoading && !partDetails) {
    return (
      <div className="flex items-center justify-center gap-2 p-6 text-sm text-slate-500">
        <LoadingWheel />
        <span>{t("Loading part images...")}</span>
      </div>
    );
  }

  if (isError && !partDetails) {
    return (
      <div className="p-4 text-sm text-yellow-800">
        {t("Unable to load full part details. Showing limited image information.")}
      </div>
    );
  }

  return <EditListingImages part={fullPart} onlyPartImages />;
}

export default function StockTable({
  showActions = true,
  showStatus = true,
  tableKey = "stock-search",
  tableUrl = "/part/stock",
  pageMode = PageMode.Stock,
  externalFilters,
  filterBanner,
  editableMpn = false,
  showQcReason = false,
  defaultPartStatus = -1,
  onAddToBasket,
  inBasketIds,
}: IStockTableProps) {
  const { t } = useTranslation("stock");
  const { addItemsMultiple } = useBaskets("StockTable");
  const { bulkPatchPart, deletePart, bulkUpdateOfferPrice } = usePartMutations();
  const { warehouseLocations } = useWarehouseLocations();
  const { workOrders } = useWorkOrders();
  const { categories: allCategories } = useCategoriesPartial();
  const { flowTypeAccounts } = useFlowTypeAccounts("StockTable");
  const { generatePartLabel } = useLabelGenerator();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers(t("Part"));
  const { addNotification } = useNotification();
  const hasTmeFeature = CheckFeatureFlag("TME");
  const queryClient = useQueryClient();
  const { register, setValue, getValues, watch, reset, handleSubmit } = useForm<IFormFields>({ defaultValues: { ...defaultFormValues, partStatus: defaultPartStatus } });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState<RowModel<IPart> | undefined>(undefined);
  const [toUpdate, setToUpdate] = useState<RowModel<IPart> | undefined>(undefined);
  const [statusUpdate, setStatusUpdate] = useState<number>(0);
  const [priceUpdate, setPriceUpdate] = useState<number>(0);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isSmartMatch, setIsSmartMatch] = useState(false);
  const [updateStatusOpen, setUpdateStatusOpen] = useState(false);
  const [updatePriceOpen, setUpdatePriceOpen] = useState(false);
  const [updateCategoryOpen, setUpdateCategoryOpen] = useState(false);
  const [updateLocationOpen, setUpdateLocationOpen] = useState(false);
  const [sendToQcOpen, setSendToQcOpen] = useState(false);
  const [partsToQc, setPartsToQc] = useState<IPart[]>([]);
  const [additionalBody, setAdditionalBody] = useState<Record<string, any>>(() => getDefaultPartStatusBody(defaultPartStatus));
  const [isSearching, setIsSearching] = useState(false);
  const [isBulkWorkOrderOpen, setIsBulkWorkOrderOpen] = useState(false);
  const [workOrderItems, setWorkOrderItems] = useState<SearchPaletteItem[]>([]);
  const [queryInput, setQueryInput] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [dates, setDates] = useState<DateRangeType>({ startDate: null, endDate: null });
  const [dateFieldType, setDateFieldType] = useState<string>("Created");
  const [queryState, setQueryState] = useState<any>(null);
  const [vehicleDetailFilterEnabled, setVehicleDetailFilterEnabled] = useState<Record<VehicleDetailFilterKey, boolean>>(defaultVehicleDetailFilterEnabled);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const ebayListingSegments = useMemo(() => {
    const visibleLabels = ["All", "Published", "Not Published"];
    const all = Object.keys(ListingStatusEnum)
      .filter((key) => isNaN(Number(key)))
      .filter((key) => visibleLabels.includes(key))
      .map((key) => ({
        value: ListingStatusEnum[key as keyof typeof ListingStatusEnum] as number,
        label: key,
      }));
    return visibleLabels.map((label) => all.find((s) => s.label === label)).filter((s): s is { value: number; label: string } => Boolean(s));
  }, []);
  const ebayAllValue = ListingStatusEnum["All"] as number;

  const partStatusOptions = useMemo(() => [
    { id: -1, name: t("All") },
    ...Object.keys(PartStatus)
      .filter((key) => isNaN(Number(key)))
      .map((key) => ({ id: PartStatus[key as keyof typeof PartStatus] as number, name: t(key) })),
  ], [t]);
  const flowAccountOptions = useMemo(
    () => (flowTypeAccounts.data?.data ?? []).map((account) => ({
      id: account.id,
      name: account.name,
    })),
    [flowTypeAccounts.data?.data]
  );
  const workOrderOptions = useMemo(
    () => [
      { id: "", name: t("All Work Orders") },
      ...(workOrders.data?.data ?? [])
        .filter((workOrder) => workOrder.status !== WorkOrderStatus.Complete)
        .map((workOrder) => ({
          id: workOrder.id,
          name: workOrder.title ? `${workOrder.orderNumber} - ${workOrder.title}` : workOrder.          orderNumber,
        })),
    ],
    [workOrders.data?.data, t]
  );

  const vehicleSearchHighlights = useMemo<VehicleSearchHighlights>(() => {
    const src: Record<string, any> = { ...(additionalBody ?? {}), ...(externalFilters ?? {}) };
    const pick = (key: string) => {
      const value = src[key];
      return value === null || value === undefined || value === "" ? undefined : value;
    };
    return {
      vrm: pick("vrm"),
      make: pick("make"),
      model: pick("model"),
      trim: pick("trim"),
      year: pick("year") ?? pick("dvlaYearOfManufacture"),
      yearFrom: pick("yearFrom"),
      yearTo: pick("yearTo"),
      bodyStyle: pick("bodyStyle"),
      numberOfDoors: pick("numberOfDoors"),
      fuelType: pick("fuelType"),
      transmissionType: pick("transmissionType"),
      engineCode: pick("engineCode"),
      engineSize: pick("engineSize"),
    };
  }, [additionalBody, externalFilters]);
  const currentSearchVrm = (watch("vrm") ?? "").toString();

  const cols = useMemo(
    () => [
      TableCheckboxCol(true, true, false, "", true),
      colHelper.display({
        id: "images",
        meta: editableMpn
          ? { thClassName: "!px-2 w-40 min-w-[10rem] max-w-[10rem]", tdClassName: "!px-2 w-40 min-w-[10rem] max-w-[10rem]" }
          : { thClassName: "!px-2 w-16", tdClassName: "!px-2 w-16" },
        cell: (info) => <PartImage part={info.row.original} width={editableMpn ? "min-w-20 max-w-40" : "min-w-10 max-w-24"} />,
      }),
      colHelper.accessor((row) => row.category?.name, {
        header: t("Part Information"),
        id: "categoryId",
        sortingFn: "auto",
        sortDescFirst: false,
        meta: { thClassName: "min-w-64 max-w-[24rem]", tdClassName: "min-w-64 max-w-[24rem] align-top" },
        cell: (info) => {
          const part = info.row.original;
          const fmt = (iso: string | null | undefined) => iso ? DateTime.fromISO(iso).toFormat("dd LLL yyyy") : null;
          const events = [
            { key: "dismantled", label: t("Dismantled"), date: fmt(part.removedOn), by: part.removedBy },
            { key: "imaged", label: t("Imaged"), date: fmt(part.imagedOn), by: part.imagedBy },
            { key: "qc", label: t("QC"), date: fmt(part.passedQcOn), by: part.passedQcBy },
          ].filter((event) => event.date || event.by);

          return (
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-800">{part.category?.name ?? "-"}</span>
                  <PartGradeBadge part={part} />
                  <PartNotesIndicator part={part} />
                  <DamageNoteIndicator part={part} />
                </div>
                {part.tagNumber && <div className="text-xs text-slate-500"><CopyText text={part.tagNumber} /></div>}
                {events.map((event) => (
                  <div key={event.key} className="flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                    <span>{event.label}{event.date ? ` ${event.date}` : ""}</span>
                    {event.by && <span className="text-slate-400">{t("by")} <ContactFromId contactId={event.by} /></span>}
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-100 pt-2 text-xs text-slate-500">
                <div className="mb-1 font-medium text-slate-600">{t("Location")}</div>
                <WarehouseLocationFromId id={part.warehouseLocationId} lastKnownId={part.lastKnownWarehouseLocationId} locatedById={part.locatedBy} lastStockChecked={part.lastStockChecked} />
              </div>
            </div>
          );
        },
      }),
      colHelper.accessor("vehicle.combinedMake", {
        header: t("Donor Vehicle"),
        enableSorting: false,
        meta: { thClassName: "max-w-[18rem] 2xl:w-96 2xl:min-w-[24rem] 2xl:max-w-[28rem]", tdClassName: "max-w-[18rem] align-top 2xl:w-96 2xl:min-w-[24rem] 2xl:max-w-[28rem]" },
        cell: (info) => <DonorVehicleCell part={info.row.original} twoColumn={editableMpn} highlights={vehicleSearchHighlights} searchVrm={currentSearchVrm} />,
      }),
      colHelper.accessor("partNumber", {
        header: t("MPN"),
        sortingFn: "auto",
        meta: editableMpn ? { thClassName: "min-w-60 max-w-[20rem]", tdClassName: "min-w-60 max-w-[20rem] align-top" } : undefined,
        cell: (info) => editableMpn
          ? <StockMpnCell part={info.row.original} />
          : info.getValue()
            ? <CopyText text={info.getValue()} />
            : <span className="text-xs text-slate-400">-</span>,
      }),
      colHelper.accessor((row) => {
        const offer = row.offers?.[0];
        const offerPrice = offer ? (offer.format === FormatType.Auction ? offer.auctionStartPrice : offer.buyItNowPrice) : null;
        return offerPrice ?? row.enteredPrice ?? 0;
      }, {
        id: "offer.price",
        header: t("Price"),
        sortingFn: (rowA: any, rowB: any) =>
          parseFloat(rowA.original.offers[0]?.auctionStartPrice ?? 0) +
          parseFloat(rowA.original.offers[0]?.buyItNowPrice ?? 0) -
          parseFloat(rowB.original.offers[0]?.auctionStartPrice ?? 0) -
          parseFloat(rowB.original.offers[0]?.buyItNowPrice ?? 0),
        cell: (info) => info.row.original.isCore
          ? <div>{PriceFormat(info.row.original.enteredPrice)}</div>
          : <UnlistedOfferPrice part={info.row.original} buttonLayout="column" hideLabel />,
      }),
      ...(showStatus ? [
        colHelper.display({
          id: "status",
          header: t("Status"),
          meta: editableMpn ? { thClassName: "min-w-[10rem] max-w-[10rem]", tdClassName: "min-w-[10rem] max-w-[10rem] align-top" } : undefined,
          cell: (info) => (
            <div>
              <StatusIcons part={info.row.original} showMarketplaces={!hasTmeFeature} />
              {/* Named pills under the icons: which marketplaces the part is on, failed on, or is
                  still going up on. The icons say the same thing but only in their tooltips. */}
              {!hasTmeFeature && <MarketplacePills part={info.row.original} />}
            </div>
          ),
        }),
      ] : []),
      ...(showQcReason ? [
        colHelper.accessor("qcReason", {
          id: "qcReason",
          header: t("QC Reason"),
          enableSorting: false,
          meta: { thClassName: "w-64 min-w-[16rem] max-w-[20rem]", tdClassName: "w-64 min-w-[16rem] max-w-[20rem] align-top" },
          cell: (info) => info.getValue()
            ? <p className="whitespace-pre-line text-xs leading-snug text-slate-700 line-clamp-4">{info.getValue()}</p>
            : <span className="text-xs text-slate-400">-</span>,
        }),
      ] : []),
      ...(onAddToBasket ? [
        colHelper.display({
          id: "basket",
          header: t("Basket"),
          meta: { thClassName: "w-32 min-w-[8rem]", tdClassName: "w-32 min-w-[8rem] align-top" },
          cell: (info) => {
            const part = info.row.original;
            const inBasket = !!part.id && !!inBasketIds?.has(part.id.toLowerCase());
            return (
              <button
                type="button"
                disabled={inBasket}
                onClick={(e) => { e.stopPropagation(); onAddToBasket(part); }}
                className={inBasket
                  ? "inline-flex h-10 items-center gap-1 rounded-md bg-emerald-50 px-3 text-sm font-medium text-emerald-700 cursor-default"
                  : "inline-flex h-10 items-center gap-1 rounded-md bg-primary-600 px-3 text-sm font-medium text-white hover:bg-primary-700"}
              >
                {inBasket ? t("In basket") : t("Add to basket")}
              </button>
            );
          },
        }),
      ] : []),
    ],
    [currentSearchVrm, editableMpn, hasTmeFeature, showQcReason, showStatus, vehicleSearchHighlights, t, onAddToBasket, inBasketIds]
  );

  const handleSendToQc = (rows: RowModel<IPart>) => {
    setPartsToQc(rows.rows.map((row) => row.original));
    setSendToQcOpen(true);
  };

  const handleMultiPrint = async (rows: RowModel<IPart>) => {
    const htmlPages = await Promise.all(rows.rows.map((row) => generatePartLabel(row.original)));
    printHtml(htmlPages.join("<div style='page-break-after: always;'></div>"));
  };

  const handleDeleteParts = async () => {
    if (!toDelete) return;
    setDeleteLoading(true);
    try {
      // allSettled, not all: one part failing used to reject the whole batch and skip the
      // success/refresh handling for the parts that did delete. And a part can delete while its
      // eBay listing stays live, so those get called out by tag number rather than folded into
      // a blanket "deleted" — that silence is why live listings on deleted parts went unnoticed.
      const results = await Promise.allSettled(
        toDelete.rows.map(async (row) => {
          const res = await deletePart.mutateAsync(row.original.id);
          row.toggleSelected(false);
          return { tagNumber: row.original.tagNumber, res };
        })
      );

      const failed = results.filter((r) => r.status === "rejected").length;
      const stillLive = results
        .filter((r): r is PromiseFulfilledResult<{ tagNumber: string; res: IDeletePartResponse }> => r.status === "fulfilled")
        .filter((r) => r.value.res?.data && !r.value.res.data.listingWithdrawn)
        .map((r) => r.value.tagNumber);

      if (failed > 0) {
        addNotification({
          variant: "error",
          primaryText: t("{{count}} part(s) could not be deleted", { count: failed }),
        });
      }

      if (stillLive.length > 0) {
        addNotification({
          variant: "warn",
          primaryText: t("{{count}} deleted part(s) are still live on a marketplace", { count: stillLive.length }),
          secondaryText: stillLive.join(", "),
        });
      }

      if (failed === 0 && stillLive.length === 0) deleteHandlers.onSuccess();

      setToDelete(undefined);
      setConfirmOpen(false);
      queryClient.invalidateQueries(["pagedTable", pageMode]);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleClearForm = () => {
    reset({ ...defaultFormValues, partStatus: defaultPartStatus });
    setIsSmartMatch(false);
    setDates({ startDate: null, endDate: null });
    setDateFieldType("Created");
    setAdditionalBody(getDefaultPartStatusBody(defaultPartStatus));
    setQueryInput("");
    setAppliedQuery("");
  };

  const handleStatusUpdate = () => {
    // Deleted isn't a status you bulk-edit parts into - hand it to the delete flow, which confirms
    // first and then deletes each part properly (Deleted/DeletedBy stamped, listings withdrawn).
    if (statusUpdate === PartStatus.Deleted) {
      setToDelete(toUpdate);
      setToUpdate(undefined);
      setUpdateStatusOpen(false);
      setStatusUpdate(0);
      setConfirmOpen(true);
      return;
    }

    bulkPatchPart.mutate({
      partIds: toUpdate?.rows.map((row) => row.original.id),
      data: { partStatus: statusUpdate },
    }, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        setToUpdate(undefined);
        setUpdateStatusOpen(false);
        setStatusUpdate(0);
      },
    });
  };

  const handlePriceUpdate = () => {
    if (isNaN(priceUpdate) || priceUpdate < 0 || priceUpdate > MAX_PART_PRICE) {
      addNotification({
        primaryText: t("Price must be between {{min}} and {{max}}", { min: PriceFormat(0), max: PriceFormat(MAX_PART_PRICE) }),
        variant: "warn",
      });
      return;
    }

    bulkUpdateOfferPrice.mutate({
      offers: toUpdate?.rows.map((row) => ({ offerId: row.original.offers[0]?.id, partId: row.original.id })),
      price: priceUpdate,
    }, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        setToUpdate(undefined);
        setUpdatePriceOpen(false);
        setPriceUpdate(0);
      },
    });
  };

  const handleAddToBasket = (rows: RowModel<IPart>) => {
    const partIds = rows.rows.map((row) => row.original.id);
    if (partIds.length > 0) addItemsMultiple.mutate(partIds);
  };

  const handleAddToWorkOrder = (rows: RowModel<IPart>) => {
    setWorkOrderItems(rows.rows.map((row) => row.original).filter((part) => Boolean(part.id)).map((part) => ({
      id: part.id!,
      name: `${part.tagNumber ?? t("Unknown Tag")} - ${part.title ?? t("Part")}`,
      type: "Stock",
    })));
    setIsBulkWorkOrderOpen(true);
  };

  const onSubmit = (data: IFormFields) => {
    if (isSearching || !data || typeof data !== "object") return;
    setIsSearching(true);

    const dataCopy: Partial<IFormFields> & Record<string, any> = { ...data, ...buildVehiclePartSearchBody(data, vehicleDetailFilterEnabled) };
    dataCopy.warehouseLocationId = data.locationSearchMode === "equals" ? data.warehouseLocation?.id : undefined;
    dataCopy.warehouseLocationBeginsWith = data.locationSearchMode === "beginsWith" ? data.warehouseLocationBeginsWith : undefined;
    dataCopy.vrm = undefined;
    dataCopy.smartSearch = undefined;
    setIsSmartMatch(false);

    const triFlags = buildTriStateFlags({
      hasWorkOrder: dataCopy.hasWorkOrder,
      hasImages: dataCopy.hasImages,
      hasMPN: dataCopy.hasMPN,
      hasLocation: dataCopy.hasLocation,
    });

    const {
      hasWorkOrder: _hasWorkOrder,
      hasImages: _hasImages,
      hasMPN: _hasMPN,
      hasLocation: _hasLocation,
      partStatus: rawPartStatus,
      partCategory,
      warehouseLocation,
      locationSearchMode,
      ...cleanData
    } = dataCopy as IFormFields;
    const normalizedCleanData = {
      ...cleanData,
      accountId: hasTmeFeature ? cleanData.accountId || undefined : undefined,
      workOrderId: cleanData.workOrderId || undefined,
    };

    setAdditionalBody({
      ...normalizedCleanData,
      startDate: dates.startDate ? toIsoDate(dates.startDate) : undefined,
      endDate: dates.endDate ? toIsoDate(dates.endDate) : undefined,
      dateFieldType,
      categoryIds: partCategory?.map((category) => category.id),
      ...triFlags,
      partStatus: typeof rawPartStatus === "number" && rawPartStatus >= 0 ? rawPartStatus : undefined,
    });
    setAppliedQuery(queryInput);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsSearching(false), 1000);
  };

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const cache = queryClient.getQueryCache();
      const pagedTableQueries = cache.getAll().filter((query) => (query.queryKey[0] === "pagedTable" || query.queryKey[0] === "vehiclePagedTable"));
      setQueryState(pagedTableQueries[pagedTableQueries.length - 1]?.state || null);
    });
    return () => unsubscribe();
  }, [queryClient]);

  const isSearchDisabled = useMemo(
    () => queryState?.fetchStatus === "fetching" || isSearching,
    [queryState, isSearching]
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setValue("partStatus", defaultPartStatus as any);
    setAdditionalBody((prev) => {
      const next = { ...prev };
      if (typeof defaultPartStatus === "number" && defaultPartStatus >= 0) {
        next.partStatus = defaultPartStatus;
      } else {
        delete next.partStatus;
      }
      return next;
    });
  }, [defaultPartStatus, setValue]);

  // Grouped explicitly rather than via useButtonGroup (which wraps every action) so
  // Delete Parts stays outside the group instead of hiding behind the toggle.
  const actionButtons = useMemo(() => [
    {
      label: t("Bulk Actions"),
      onClick: () => { },
      groupedButtons: [
        { label: t("Change Category"), onClick: (rows: RowModel<IPart>) => { setToUpdate(rows); setUpdateCategoryOpen(true); }, disabledOnNoRows: true },
        { label: t("Change Warehouse Location"), onClick: (rows: RowModel<IPart>) => { setToUpdate(rows); setUpdateLocationOpen(true); }, disabledOnNoRows: true },
        { label: t("Send to QC"), onClick: handleSendToQc, disabledOnNoRows: true },
        { label: t("Reprint Labels"), onClick: handleMultiPrint, disabledOnNoRows: true },
        { label: t("Change Part Status"), disabledOnNoRows: true, onClick: (rows: RowModel<IPart>) => { setToUpdate(rows); setUpdateStatusOpen(true); } },
        { label: t("Change Price"), disabledOnNoRows: true, onClick: (rows: RowModel<IPart>) => { setToUpdate(rows); setUpdatePriceOpen(true); } },
        { label: t("Add To Basket"), disabledOnNoRows: true, onClick: handleAddToBasket },
        { label: t("Add to work order"), disabledOnNoRows: true, onClick: handleAddToWorkOrder },
      ],
    },
    { label: t("Delete Parts"), disabledOnNoRows: true, onClick: (rows: RowModel<IPart>) => { setToDelete(rows); setConfirmOpen(true); } },
  ], [handleMultiPrint, handleAddToBasket, handleAddToWorkOrder, t]);

  const watchHasWorkOrder = watch("hasWorkOrder");
  const watchHasImages = watch("hasImages");
  const watchHasMPN = watch("hasMPN");
  const watchHasLocation = watch("hasLocation");
  const watchListingStatus = watch("listingStatus");
  const watchPartStatus = watch("partStatus");
  const watchAccountId = watch("accountId");
  const watchWorkOrderId = watch("workOrderId");
  const watchWarehouseLocation = watch("warehouseLocation");
  const watchWarehouseLocationBeginsWith = watch("warehouseLocationBeginsWith");

  const activeFilterPills = useMemo(() => {
    const pills: { key: string; label: string; onClear: () => void }[] = [];
    const triLabel = (label: string, value: TriState | undefined) => value && value !== "any" ? `${label}: ${value === "with" ? t("With") : t("Without")}` : undefined;

    if (typeof watchListingStatus === "number" && watchListingStatus !== ebayAllValue) {
      pills.push({
        key: "listingStatus",
        label: `eBay: ${t(ebayListingSegments.find((segment) => segment.value === watchListingStatus)?.label ?? "Selected")}`,
        onClear: () => setValue("listingStatus", ebayAllValue as any),
      });
    }

    if (typeof watchPartStatus === "number" && watchPartStatus >= 0) {
      pills.push({
        key: "partStatus",
        label: `${t("Status")}: ${partStatusOptions.find((option) => option.id === watchPartStatus)?.name ?? t("Selected")}`,
        onClear: () => setValue("partStatus", -1 as any),
      });
    }

    if (hasTmeFeature && watchAccountId) {
      pills.push({
        key: "accountId",
        label: `${t("Account")}: ${flowAccountOptions.find((option) => option.id === watchAccountId)?.name ?? t("Selected")}`,
        onClear: () => setValue("accountId", undefined as any),
      });
    }

    if (watchWorkOrderId) {
      pills.push({
        key: "workOrderId",
        label: `${t("Work Order")}: ${workOrderOptions.find((option) => option.id === watchWorkOrderId)?.name ?? t("Selected")}`,
        onClear: () => setValue("workOrderId", "" as any),
      });
    }

    if (watchWarehouseLocation?.id || watchWarehouseLocationBeginsWith) {
      pills.push({
        key: "warehouseLocation",
        label: `${t("Location")}: ${watchWarehouseLocation?.displayName ?? watchWarehouseLocationBeginsWith}`,
        onClear: () => {
          setValue("warehouseLocation", undefined as any);
          setValue("warehouseLocationId", undefined as any);
          setValue("warehouseLocationBeginsWith", "" as any);
        },
      });
    }

    if (dates.startDate || dates.endDate) {
      pills.push({
        key: "dates",
        label: `${t("Dates")}: ${dateFieldType}`,
        onClear: () => setDates({ startDate: null, endDate: null }),
      });
    }

    [
      { key: "hasWorkOrder", label: triLabel(t("Work Order"), watchHasWorkOrder), clear: () => setValue("hasWorkOrder", "any" as any) },
      { key: "hasImages", label: triLabel(t("Images"), watchHasImages), clear: () => setValue("hasImages", "any" as any) },
      { key: "hasMPN", label: triLabel(t("MPN"), watchHasMPN), clear: () => setValue("hasMPN", "any" as any) },
      { key: "hasLocation", label: triLabel(t("Location"), watchHasLocation), clear: () => setValue("hasLocation", "any" as any) },
    ].forEach((pill) => {
      if (!pill.label) return;
      pills.push({ key: pill.key, label: pill.label, onClear: pill.clear });
    });

    return pills;
  }, [watchListingStatus, watchPartStatus, watchAccountId, watchWorkOrderId, watchWarehouseLocation?.id, watchWarehouseLocation?.displayName, watchWarehouseLocationBeginsWith, dates.startDate, dates.endDate, dateFieldType, watchHasWorkOrder, watchHasImages, watchHasMPN, watchHasLocation, hasTmeFeature, ebayAllValue, ebayListingSegments, partStatusOptions, flowAccountOptions, workOrderOptions, setValue, t]);

  const tableAdditionalBody = useMemo(() => ({ ...additionalBody, ...externalFilters }), [additionalBody, externalFilters]);
  const defaultFiltersStorageKey = `wilma:tableFilters:${tableKey}`;

  const getDefaultFilters = useCallback((): IStockSavedDefaultFilters => ({
    listingStatus: watchListingStatus,
    partStatus: defaultPartStatus !== undefined ? defaultPartStatus : watchPartStatus,
    hasWorkOrder: watchHasWorkOrder,
    hasImages: watchHasImages,
    hasMPN: watchHasMPN,
    hasLocation: watchHasLocation,
    locationSearchMode: watch("locationSearchMode"),
    warehouseLocation: watchWarehouseLocation,
    warehouseLocationBeginsWith: watchWarehouseLocationBeginsWith,
    startDate: dates.startDate ? toIsoDate(dates.startDate) : null,
    endDate: dates.endDate ? toIsoDate(dates.endDate) : null,
    dateFieldType,
    accountId: hasTmeFeature ? watch("accountId") : undefined,
    workOrderId: watchWorkOrderId,
  }), [watch, watchListingStatus, watchPartStatus, watchHasWorkOrder, watchHasImages, watchHasMPN, watchHasLocation, watchWarehouseLocation, watchWarehouseLocationBeginsWith, dates.startDate, dates.endDate, dateFieldType, hasTmeFeature, watchWorkOrderId]);

  const applyDefaultFilters = useCallback((savedFilters: IStockSavedDefaultFilters) => {
    // With the eBay Status control hidden there is no way to clear a saved listing filter, so a
    // filter saved before it was hidden would quietly cut the result set. Force it back to All.
    setValue("listingStatus", (hasTmeFeature ? ebayAllValue : savedFilters.listingStatus) as any);
    setValue("partStatus", savedFilters.partStatus as any);
    setValue("hasWorkOrder", savedFilters.hasWorkOrder as any);
    setValue("hasImages", savedFilters.hasImages as any);
    setValue("hasMPN", savedFilters.hasMPN as any);
    setValue("hasLocation", savedFilters.hasLocation as any);
    setValue("locationSearchMode", (savedFilters.locationSearchMode ?? "equals") as any);
    setValue("warehouseLocation", savedFilters.warehouseLocation as any);
    setValue("warehouseLocationId", savedFilters.warehouseLocation?.id as any);
    setValue("warehouseLocationBeginsWith", (savedFilters.warehouseLocationBeginsWith ?? "") as any);
    setValue("accountId", (hasTmeFeature ? savedFilters.accountId : undefined) as any);
    setValue("workOrderId", (savedFilters.workOrderId ?? "") as any);

    const savedDates: DateRangeType = {
      startDate: savedFilters.startDate ? new Date(savedFilters.startDate) : null,
      endDate: savedFilters.endDate ? new Date(savedFilters.endDate) : null,
    };
    const savedDateFieldType = savedFilters.dateFieldType ?? "Created";
    const triFlags = buildTriStateFlags({
      hasWorkOrder: savedFilters.hasWorkOrder,
      hasImages: savedFilters.hasImages,
      hasMPN: savedFilters.hasMPN,
      hasLocation: savedFilters.hasLocation,
    });

    setDates(savedDates);
    setDateFieldType(savedDateFieldType);
    setAdditionalBody({
      listingStatus: savedFilters.listingStatus,
      locationSearchMode: savedFilters.locationSearchMode,
      warehouseLocationId: savedFilters.locationSearchMode === "equals" ? savedFilters.warehouseLocation?.id : undefined,
      warehouseLocationBeginsWith: savedFilters.locationSearchMode === "beginsWith" ? savedFilters.warehouseLocationBeginsWith || undefined : undefined,
      accountId: hasTmeFeature ? savedFilters.accountId || undefined : undefined,
      workOrderId: savedFilters.workOrderId || undefined,
      startDate: savedDates.startDate ? toIsoDate(savedDates.startDate) : undefined,
      endDate: savedDates.endDate ? toIsoDate(savedDates.endDate) : undefined,
      dateFieldType: savedDateFieldType,
      ...triFlags,
      partStatus: typeof savedFilters.partStatus === "number" && savedFilters.partStatus >= 0 ? savedFilters.partStatus : undefined,
    });
  }, [ebayAllValue, hasTmeFeature, setValue]);

  return (
    <>
      <ChangeCategoryModal open={updateCategoryOpen} setOpen={setUpdateCategoryOpen} setToUpdate={setToUpdate} parts={toUpdate?.rows.map((row) => row.original)!} />
      <ChangeWarehouseLocationModal open={updateLocationOpen} setOpen={setUpdateLocationOpen} setToUpdate={setToUpdate} parts={toUpdate?.rows.map((row) => row.original)!} />
      <SendToQcModal
        open={sendToQcOpen}
        setOpen={setSendToQcOpen}
        parts={partsToQc}
        onCompleted={() => {
          saveHandlers.onSuccess();
          queryClient.invalidateQueries(["pagedTable", pageMode]);
        }}
        onFailed={saveHandlers.onError}
      />
      <Modal open={updateStatusOpen} setOpen={setUpdateStatusOpen} width="max-w-3xl">
        <div className="space-y-4">
          <p>{t("You are about to update {{count}} parts!", { count: toUpdate?.rows.length })}</p>
          <div className="grid w-full grid-cols-1 gap-2 lg:grid-cols-2">
            <select value={statusUpdate} className="border p-1" onChange={(e) => setStatusUpdate(parseInt(e.target.value))}>
              {Object.values(PartStatus).filter((status) => isNaN(Number(status))).map((status, index) => (
                <option key={status} value={index}>{t(String(status))}</option>
              ))}
            </select>
            <ButtonInput label={t("Update All")} classes="" onClick={handleStatusUpdate} />
          </div>
        </div>
      </Modal>
      <Modal open={updatePriceOpen} setOpen={setUpdatePriceOpen} width="max-w-3xl">
        <div className="space-y-4">
          <p>{t("You are about to update {{count}} parts!", { count: toUpdate?.rows.length })}</p>
          <div className="grid w-full grid-cols-1 gap-2 lg:grid-cols-2">
            <div className="flex w-full items-center">
              {currencySymbol()} <input step={0.01} min={0} max={MAX_PART_PRICE} defaultValue={priceUpdate} className="h-8 w-full border p-1 text-sm" type="number" onChange={(e) => setPriceUpdate(parseFloat(e.target.value))} />
            </div>
            <ButtonInput label={t("Update All")} classes="" onClick={handlePriceUpdate} />
          </div>
        </div>
      </Modal>
      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title={t("Delete these parts?")}
        message={t("Are you sure you want to delete these parts? \n This action cannot be undone.")}
        confirmButtonText={t("Delete")}
        onConfirm={handleDeleteParts}
        isLoading={deleteLoading}
      />
      <BulkAddToWorkOrderModal open={isBulkWorkOrderOpen} onClose={() => { setIsBulkWorkOrderOpen(false); setWorkOrderItems([]); }} selectedItems={workOrderItems} />

      <form onSubmit={handleSubmit(onSubmit)} className="my-2 mb-4 space-y-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <VehiclePartSearchFilters
          register={register}
          setValue={setValue}
          getValues={getValues}
          watch={watch}
          categories={allCategories.data?.data ?? []}
          categoriesLoading={allCategories.isLoading}
          disabled={isSearchDisabled}
          isSearching={isSearching}
          setIsSearching={setIsSearching}
          isSmartMatch={isSmartMatch}
          setIsSmartMatch={setIsSmartMatch}
          vehicleDetailFilterEnabled={vehicleDetailFilterEnabled}
          setVehicleDetailFilterEnabled={setVehicleDetailFilterEnabled}
          onApplySmartSearch={setAdditionalBody}
          getAdditionalSmartSearchBody={(data) => ({ listingStatus: data.listingStatus })}
        />

        <hr className="mt-1" />

        <TableFilterPanel
          activeFilterPills={activeFilterPills}
          disabled={isSearchDisabled}
          defaultFiltersStorageKey={defaultFiltersStorageKey}
          getDefaultFilters={getDefaultFilters}
          onApplyDefaultFilters={applyDefaultFilters}
        >
          <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
            {!hasTmeFeature && (
              <FilterSegmented<number> label={t("eBay Status")} value={watchListingStatus ?? ebayAllValue} onChange={(value) => setValue("listingStatus", value as any)} segments={ebayListingSegments} disabled={isSearchDisabled} />
            )}
            <TriToggleInput label={t("Work Order")} value={watchHasWorkOrder} onChange={(value) => setValue("hasWorkOrder", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label={t("Images")} value={watchHasImages} onChange={(value) => setValue("hasImages", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label={t("MPN")} value={watchHasMPN} onChange={(value) => setValue("hasMPN", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label={t("Location")} value={watchHasLocation} onChange={(value) => setValue("hasLocation", value as any)} disabled={isSearchDisabled} />
            <WarehouseLocationSearch showSearchOptions register={register} setValue={setValue} watch={watch} isSearchDisabled={isSearchDisabled} warehouseLocations={warehouseLocations} />
            <DateRangeFilter dates={dates} onDatesChange={setDates} dateFieldType={dateFieldType} onDateFieldTypeChange={setDateFieldType} disabled={isSearchDisabled} />
            <div className="w-44">
              <SelectInput label={t("Part Status")} register={register} registerName="partStatus" options={partStatusOptions} setValue={setValue} watch={watch} cols="w-full" disabled={isSearchDisabled} rounded />
            </div>
            <div className="w-52">
              <SelectInput label={t("Work Order")} register={register} registerName="workOrderId" options={workOrderOptions} setValue={setValue} watch={watch} cols="w-full" disabled={isSearchDisabled} rounded />
            </div>
            {hasTmeFeature && (
              <div className="w-52">
                <SelectInput
                  label={t("Account")}
                  register={register}
                  registerName="accountId"
                  options={flowAccountOptions}
                  setValue={setValue}
                  watch={watch}
                  cols="w-full"
                  disabled={isSearchDisabled}
                  rounded
                />
              </div>
            )}
          </div>
        </TableFilterPanel>

        <div className="relative flex flex-wrap items-center gap-x-4 gap-y-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <input
            type="text"
            value={queryInput}
            disabled={isSearchDisabled}
            onChange={(e) => setQueryInput(e.target.value)}
            className="max-w-full rounded-md border-1 border-gray-300 pl-10 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:min-w-[unset] sm:text-sm lg:min-w-[250px] lg:max-w-[calc(100%-3rem)]"
            placeholder={t("Search stock by Tag, Part No., Make, Model, Engine Code, Category, or Warehouse")}
            style={{ width: `calc(${t("Search stock by Tag, Part No., Make, Model, Engine Code, Category, or Warehouse").length}ch - 2rem)` }}
          />
        </div>

        <div className="flex items-center gap-x-4 pt-1">
          <ButtonInput
            label={isSearching ? t("Searching...") : t("Search")}
            isSubmit
            classes={classNames(isSearchDisabled ? "" : "!bg-primary-600 hover:!bg-primary-700")}
            onClick={undefined}
            disabled={isSearchDisabled}
            isLoading={isSearching}
          />
          <ButtonInput
            label={t("Clear")}
            classes={classNames(isSearchDisabled ? "" : "!bg-white !text-gray-700 !border !border-gray-300 hover:!bg-gray-100")}
            onClick={handleClearForm}
            disabled={isSearchDisabled}
          />
        </div>
      </form>

      {filterBanner}

      <VehiclePagedTable
        key={tableKey}
        url={tableUrl}
        columns={cols}
        pageMode={pageMode}
        queryLabel={undefined}
        externalQuery={appliedQuery}
        requireLoad
        // onRowDoubleClick={(part: IPart) => openInNewTab(`stock/${part.id}`)}
        onRowClick={(part: IPart) => openInNewTab(`stock/${part.id}`)}
        actionButtons={showActions ? actionButtons : []}
        showTotal
        showDateFilter={false}
        additionalBody={tableAdditionalBody}
        expandedRow={(part) => <StockImagesExpandedRow part={part} />}
      />

    </>
  );
}
